import { siteConfig } from "@/config/site";
import { Picture } from "@/components/media/Picture";
import { Eyebrow, PrimaryCta, SecondaryCta } from "./primitives";

/**
 * Above-the-fold hero. Everything a first-time visitor needs in one viewport:
 * who we are, where we work, what we do, and the two standard actions.
 *
 * The photograph is the LCP element and is loaded eagerly at high priority.
 */
export function HomeHeroV2() {
  const { city, state } = siteConfig.contact.address;

  return (
    <section aria-labelledby="hero-heading" className="relative overflow-hidden">
      <div className="container-page grid items-center gap-8 py-6 md:py-10 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:py-14">
        {/* Copy */}
        <div className="max-w-2xl">
          <div className="animate-op-fade">
            <Eyebrow>
              {city} SEO Agency · Local Search · AI Search
            </Eyebrow>
          </div>

          <h1
            id="hero-heading"
            className="animate-op-fade-up mt-6 text-balance font-display text-4xl leading-[1.02] sm:text-5xl lg:text-6xl xl:text-[4.25rem]"
          >
            Search growth for {city} businesses —
            <span className="block text-primary">
              engineered, measured, compounding.
            </span>
          </h1>

          <p
            className="animate-op-fade-up mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
            style={{ animationDelay: "0.08s" }}
          >
            {siteConfig.name} is an {siteConfig.tagline.toLowerCase()} combining
            technical SEO, local search, content, authority building and AI-search
            optimization into one strategy — so the right customers find you across
            Google, Maps and the answer engines they now ask first.
          </p>

          <div
            className="animate-op-fade-up mt-9 flex flex-wrap items-center gap-3"
            style={{ animationDelay: "0.16s" }}
          >
            <PrimaryCta />
            <SecondaryCta />
          </div>

          <p
            className="animate-op-fade mt-5 text-xs text-muted-foreground"
            style={{ animationDelay: "0.24s" }}
          >
            Founder-led strategy from {city}, {state}. Serving Central Texas and
            beyond.
          </p>
        </div>

        {/* Visual */}
        <div
          className="animate-op-scale-in relative"
          style={{ animationDelay: "0.1s" }}
        >
          <div
            aria-hidden
            className="ambient-blob -right-16 -top-20 h-72 w-72"
            style={{ background: "oklch(0.58 0.228 27 / 0.28)" }}
          />
          <figure className="relative overflow-hidden rounded-2xl border border-border bg-surface-2">
            <Picture
              variantKey="home-hero-v2"
              alt={`A search strategist reviewing organic performance dashboards at ${siteConfig.name} in ${city}, ${state}`}
              width={1600}
              height={1000}
              priority
              sizes="(min-width: 1024px) 620px, 100vw"
            />
            <figcaption className="sr-only">
              Search performance analysis at {siteConfig.name}.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}
